import React, { useState } from 'react';
import { Check, X, Sparkles, Zap, Crown } from 'lucide-react';
import { PRICING_PLANS, PricingPlan } from '@/config/pricing';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useSubscription } from '@/contexts/SubscriptionContext';

interface PricingPlansProps {
  currentPlan?: string;
  onSelectPlan?: (plan: PricingPlan) => void;
  showBilling?: boolean;
}

export const PricingPlans: React.FC<PricingPlansProps> = ({
  currentPlan: _currentPlan,
  onSelectPlan,
  showBilling = true,
}) => {
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const { subscription } = useSubscription();
  // Use subscription context instead of prop (prop is kept for backward compatibility)
  const currentPlan = subscription?.plan_id || 'free';

  // Log subscription data for debugging
  console.log('[PricingPlans] subscription:', {
    plan_id: subscription?.plan_id,
    status: subscription?.status,
    currentPlanVar: currentPlan,
  });

  const handleSelectPlan = (plan: PricingPlan) => {
    if (onSelectPlan) {
      onSelectPlan(plan);
    } else {
      toast.info(`Selected ${plan.name} plan`);
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'free':
        return <Sparkles className="h-6 w-6" />;
      case 'starter':
        return <Zap className="h-6 w-6" />;
      case 'pro':
        return <Crown className="h-6 w-6" />;
      default:
        return <Sparkles className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId: string) => {
    switch (planId) {
      case 'free':
        return 'text-slate-600';
      case 'starter':
        return 'text-blue-600';
      case 'pro':
        return 'text-purple-600';
      default:
        return 'text-slate-600';
    }
  };

  const formatPrice = (price: number, interval: 'month' | 'year') => {
    const yearlyDiscount = 0.2; // 20% discount for yearly
    const displayPrice = interval === 'year' ? price * 12 * (1 - yearlyDiscount) : price;
    return displayPrice;
  };

  return (
    <TooltipProvider>
      <div className="space-y-8">
        {/* Billing Toggle */}
        {showBilling && (
          <div className="flex items-center justify-center gap-4">
            <Label htmlFor="billing-toggle" className={cn(
              "text-sm font-medium",
              billingInterval === 'month' && 'text-primary'
            )}>
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={billingInterval === 'year'}
              onCheckedChange={(checked) => setBillingInterval(checked ? 'year' : 'month')}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="billing-toggle" className={cn(
                "text-sm font-medium",
                billingInterval === 'year' && 'text-primary'
              )}>
                Yearly
              </Label>
              {billingInterval === 'year' && (
                <Badge variant="secondary" className="text-xs">
                  Save 20%
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
          {PRICING_PLANS.map((plan) => {
            // Plan hierarchy: free < starter < pro < enterprise
            const planHierarchy = { free: 0, starter: 1, pro: 2, enterprise: 3 };
            const currentPlanLevel = planHierarchy[currentPlan] || 0;
            const selectedPlanLevel = planHierarchy[plan.id] || 0;

            const isCurrentPlan = currentPlan === plan.id && subscription?.status === 'active';
            const isUpgrade = selectedPlanLevel > currentPlanLevel;
            const isDowngrade = selectedPlanLevel < currentPlanLevel;

            console.log('[PricingPlans] Plan comparison:', {
              plan: plan.id,
              currentPlan,
              isCurrentPlan,
              isUpgrade,
              isDowngrade,
              status: subscription?.status,
            });

            // Determine button text and state
            let buttonText = `Get ${plan.name}`;
            let buttonDisabled = isCurrentPlan;
            let buttonVariant: 'default' | 'outline' | 'secondary' = 'outline';

            if (isCurrentPlan) {
              buttonText = 'Current Plan';
              buttonDisabled = true;
              buttonVariant = 'secondary';
            } else if (isUpgrade) {
              buttonText = `Upgrade to ${plan.name}`;
              buttonDisabled = false;
              buttonVariant = 'default';
            } else if (isDowngrade) {
              // Show message that user is on higher plan instead of downgrade option
              const currentPlanDisplayName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);
              buttonText = `You are already on ${currentPlanDisplayName} Plan`;
              buttonDisabled = true;
              buttonVariant = 'outline';
            }

            const displayPrice = formatPrice(plan.price, billingInterval);

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative transition-all hover:shadow-lg",
                  plan.popular && "border-primary shadow-md scale-105",
                  isCurrentPlan && "ring-2 ring-primary"
                )}
              >
                {/* Popular Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-0 right-0 flex justify-center">
                    <Badge className="px-3 py-1">{plan.badge}</Badge>
                  </div>
                )}

                {/* Status Badge - Only show for current plan */}
                {isCurrentPlan && (
                  <div className={cn(
                    "absolute -top-3 right-4",
                    "bg-green-600 text-white"
                  )}>
                    <Badge
                      variant="default"
                      className="px-2 py-1 text-xs bg-green-600"
                    >
                      ✓ Current Plan
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-8">
                  <div className={cn("mx-auto mb-4", getPlanColor(plan.id))}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      ${billingInterval === 'year' ? Math.round(displayPrice / 12) : displayPrice}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      /{billingInterval === 'year' ? 'month' : plan.interval}
                    </span>
                    {billingInterval === 'year' && (
                      <div className="text-sm text-muted-foreground mt-1">
                        ${Math.round(displayPrice)} billed annually
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pb-8">
                  {/* Key Limits */}
                  <div className="space-y-2 mb-6 pb-6 border-b">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Requests</span>
                      <span className="font-medium">
                        {plan.limits.requests === 'unlimited' ? 'Unlimited' : `${plan.limits.requests}/month`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">DB Connections</span>
                      <span className="font-medium">
                        {plan.limits.dbConnections === 'unlimited' ? 'Unlimited' : plan.limits.dbConnections}
                      </span>
                    </div>
                    {plan.limits.tokens && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tokens</span>
                        <span className="font-medium">
                          {(plan.limits.tokens / 1000000).toFixed(0)}M/month
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Features List */}
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-gray-300 mt-0.5 flex-shrink-0" />
                        )}
                        <span
                          className={cn(
                            "text-sm",
                            !feature.included && "text-muted-foreground line-through"
                          )}
                        >
                          {feature.text}
                          {feature.tooltip && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="ml-1 text-xs text-muted-foreground cursor-help">ⓘ</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{feature.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full">
                        <Button
                          className="w-full"
                          variant={buttonVariant}
                          disabled={buttonDisabled}
                          onClick={() => handleSelectPlan(plan)}
                        >
                          {buttonText}
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {isDowngrade && (
                      <TooltipContent>
                        <p className="text-sm">You're on a higher plan. To downgrade, please contact support.</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="text-center space-y-4 pt-8 border-t">
          <p className="text-sm text-muted-foreground">
            All plans include SSL encryption, 99.9% uptime SLA, and automatic backups
          </p>
          <div className="flex items-center justify-center gap-4 text-sm">
            <Button variant="link" className="text-muted-foreground">
              Compare all features
            </Button>
            <Button variant="link" className="text-muted-foreground">
              Contact sales
            </Button>
            <Button variant="link" className="text-muted-foreground">
              FAQ
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};